import React, { useState, useCallback, useEffect } from 'react';
import { Card, List, Empty, Input, Typography, Spin, message } from 'antd';
import { FileTextOutlined, FileAddOutlined, EditOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAccidentReportResponses } from '../utils/apiService';
import { formatDate } from '../utils/dateUtils';

const { Search } = Input;
const { Text } = Typography;

const PAGE_SIZE = 10;

export const AccidentReportsList = ({ isCompleted, navigate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async (page = 0, search = '') => {
    setLoading(true);
    try {
      const res = await getAccidentReportResponses({
        pageSize: PAGE_SIZE,
        pageIndex: page,
        isCompleted,
        searchTerm: search,
      });
      const list = Array.isArray(res) ? res : (res?.items || []);
      setItems(list);
      setTotalCount(typeof res?.totalCount === 'number' ? res.totalCount : list.length);
      setPageIndex(page);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setItems([]);
        setTotalCount(0);
      } else {
        console.error('Failed to load accident report responses', err);
        message.error('Failed to load accident reports');
      }
    } finally {
      setLoading(false);
    }
  }, [isCompleted]);

  useEffect(() => {
    load(0, '');
  }, [load]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    load(0, value);
  };

  const handlePageChange = (page) => {
    load(page - 1, searchTerm);
  };

  return (
    <div>
      <Search
        placeholder="Search by registration, fleet, company..."
        loading={loading}
        allowClear
        onSearch={handleSearch}
        style={{ marginBottom: 16 }}
      />
      <List
        itemLayout="horizontal"
        dataSource={items}
        loading={loading}
        pagination={
          totalCount > PAGE_SIZE
            ? {
                current: pageIndex + 1,
                pageSize: PAGE_SIZE,
                total: totalCount,
                showSizeChanger: false,
                onChange: handlePageChange,
              }
            : false
        }
        renderItem={(item) => (
          <List.Item
            key={item.id}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              navigate(`/inspections/responses/${item.id}`, {
                state: { from: 'accidentReports', editable: !isCompleted },
              })
            }
          >
            <List.Item.Meta
              avatar={<FileTextOutlined style={{ fontSize: 20 }} />}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{item?.inspectionName || 'Accident Report'}</span>
                  <RightOutlined style={{ color: '#999', fontSize: 12 }} />
                </div>
              }
              description={
                <>
                  <Text type="secondary">Registration: {item?.vehicle?.registration || 'N/A'}</Text>
                  <br />
                  <Text type="secondary">Fleet: {item?.vehicle?.fleet || 'N/A'}</Text>
                  <br />
                  <Text type="secondary">Created: {formatDate(item.createdDateTime, true)}</Text>
                  <br />
                  <Text type="secondary">Company: {item?.companyName || 'N/A'}</Text>
                  {item?.createdByName ? (
                    <>
                      <br />
                      <Text type="secondary">By: {item.createdByName}</Text>
                    </>
                  ) : null}
                </>
              }
            />
          </List.Item>
        )}
        locale={{
          emptyText: loading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Spin />
              <div style={{ marginTop: 8, fontWeight: 500 }}>Loading...</div>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={searchTerm ? 'No matching accident reports found' : (isCompleted ? 'No accident report history' : 'No drafts')}
            />
          ),
        }}
      />
    </div>
  );
};

const AccidentReports = () => {
  const navigate = useNavigate();

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div
          className="action-card"
          onClick={() => navigate('/accidentReports/new')}
          style={{
            backgroundColor: '#f5f5f5',
            color: '#333',
            borderRadius: '8px',
            height: '100px',
            display: 'flex',
            flex: '1',
            minWidth: '120px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <FileAddOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
          <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>New</div>
        </div>
        <div
          className="action-card"
          onClick={() => navigate('/accidentReports/drafts')}
          style={{
            backgroundColor: '#f5f5f5',
            color: '#333',
            borderRadius: '8px',
            height: '100px',
            display: 'flex',
            flex: '1',
            minWidth: '120px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <EditOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
          <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Drafts</div>
        </div>
      </div>
      <br />
      <Card title="History">
        <AccidentReportsList isCompleted={true} navigate={navigate} />
      </Card>
    </div>
  );
};

export default AccidentReports;
